import { useTranslate } from '@/hooks'
import { useAppSelector } from '@/redux/hooks'
import { LanguageSelect } from './language-select'
import { LoginForm } from './login-form'
import { TermsAndConditions } from './terms-and-conditions'
import { PortalFooter } from '@/ui/layout/portal-footer'
import { MisakoFooter } from '@/ui/custom/misako-footer'
import { MisakoHeader } from '@/ui/custom/misako-header'
import { HoffHeader } from '@/ui/custom/hoff-header'

// children replace the landing copy and the login form, so other entry points keep the merchant's landing.
export const VerticalLoginLayout = ({
  children
}: {
  children?: React.ReactNode
}) => {
  const { translate } = useTranslate()

  const settings = useAppSelector(
    (store) => store.settingsApi.settings.response
  )

  const logoUrl = settings.branding?.logo_url
  const title = settings.branding?.title
  const backgroundUrl = settings.branding?.background_image_url
  const desktopLogoHeight = settings.branding?.logo_height_desktop
  const mobileLogoHeight = settings.branding?.logo_height_mobile

  const isHoff = settings.ecommerce_group === 'hoff'
  const isMisako = settings.ecommerce_group == 'misako'

  if (isHoff) {
    return (
      <div className="relative flex h-dvh flex-col">
        <HoffHeader />
        <div className="relative mx-auto mt-20 flex items-center justify-center rounded-xl p-4">
          <div className="flex h-fit w-full flex-col items-center justify-center gap-5 bg-white p-5 md:p-8">
            <div className="mb-2 flex w-full flex-1 flex-col items-center justify-center gap-5 md:mb-0">
              {children ?? (
                <>
                  <header className="mb-2 space-y-2 text-center">
                    <h1
                      className="text-xl font-light"
                      dangerouslySetInnerHTML={{
                        __html: translate('landing_title_layout2')
                      }}
                    />
                    <h2
                      className="mx-auto text-base font-light [text-wrap:pretty]"
                      dangerouslySetInnerHTML={{
                        __html: translate('landing_subtitle')
                      }}
                    />
                  </header>
                  <LoginForm />
                  <TermsAndConditions />
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (isMisako) {
    return (
      <div className="relative flex h-full min-h-screen flex-col">
        <MisakoHeader />
        <div className="flex flex-1 flex-col items-center justify-center p-4 md:px-10 md:py-6">
          <div className="flex h-fit w-full max-w-[33rem] flex-1 flex-col items-center justify-center gap-5 rounded-md bg-white md:h-full md:p-0 md:py-32">
            <div className="mb-2 flex w-full flex-1 flex-col items-center justify-center gap-6 md:mb-0">
              {children ?? (
                <>
                  <header className="mb-2 space-y-4 text-center">
                    <h1
                      className="text-xl tracking-wider [text-wrap:balance] md:text-3xl"
                      dangerouslySetInnerHTML={{
                        __html: translate('landing_title_layout2')
                      }}
                    />
                    <h2
                      className="mx-auto text-sm font-[300] tracking-wider [text-wrap:pretty]"
                      dangerouslySetInnerHTML={{
                        __html: translate('landing_subtitle')
                      }}
                    />
                  </header>
                  <LoginForm />
                  <TermsAndConditions />
                </>
              )}
            </div>
          </div>
        </div>
        <MisakoFooter />
      </div>
    )
  }

  return (
    <div className="relative grid h-dvh grid-cols-1 md:grid-cols-2">
      <div className="absolute right-4 top-4 z-10 w-32">
        <LanguageSelect />
      </div>
      <div className="h-dvh w-full bg-neutral-200 [grid-area:1/1] md:[grid-area:1/1]">
        {backgroundUrl ? (
          <img src={backgroundUrl} className="size-full object-cover" alt="" />
        ) : null}
      </div>
      <div className="relative flex items-center justify-center p-4 [grid-area:1/1] md:[grid-area:1/2]">
        <div className="flex h-fit w-full max-w-[26rem] flex-col items-center justify-center gap-5 rounded-md bg-white p-5 shadow-md md:h-full md:p-0 md:shadow-none">
          <div className="mb-2 flex w-full flex-1 flex-col items-center justify-center gap-5 md:mb-0">
            {logoUrl ? (
              <div className="mx-auto pb-2">
                <img
                  style={
                    {
                      '--logo-desktop-height': desktopLogoHeight ?? '2rem',
                      '--logo-mobile-height': mobileLogoHeight ?? '1.5rem'
                    } as React.CSSProperties
                  }
                  width={285}
                  height={70}
                  src={logoUrl}
                  alt={title}
                  className="h-[var(--logo-mobile-height)] w-auto object-contain md:h-[var(--logo-desktop-height)]"
                />
              </div>
            ) : null}
            {children ?? (
              <>
                <header className="mb-2 space-y-2 text-center">
                  <h1
                    className="text-xl [font-weight:750] [text-wrap:balance] md:text-2xl"
                    dangerouslySetInnerHTML={{
                      __html: translate('landing_title_layout2')
                    }}
                  />
                  <h2
                    className="mx-auto max-w-96 text-sm [font-weight:normal] [text-wrap:pretty]"
                    dangerouslySetInnerHTML={{
                      __html: translate('landing_subtitle')
                    }}
                  />
                </header>
                <LoginForm />
                <TermsAndConditions />
              </>
            )}
          </div>
          <PortalFooter />
        </div>
      </div>
    </div>
  )
}
